import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import ItemHub from '../../components/hub/ItemHub';
import { isWowcherIE } from '../../helpers/pages';
import { isMobileViewport, useScreenBreakpoint } from '../../helpers/screen';
import { getNearMeHub } from '../../helpers/url';
import ThemeContext from '../../providers/ThemeProvider';
import { getServerSideEssentials } from '../../redux/actions/ssr';
import { wrapper } from '../../redux/store/store';

const Form404Block = dynamic(() =>
  import('../../components/errors/Form404Block'),
);

const NearMePage = ({ data }) => {
  const theme = useContext(ThemeContext);
  const breakpoint = useScreenBreakpoint();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileViewport());
  }, [breakpoint]);

  if (isWowcherIE()) {
    return <Form404Block statusCode={404} />;
  }

  return (
    <>
      <div className="rel-container">
        <div className="container">
          <div className="seo__content">
            <div className="hero-image" id="seo-hero-image" />
            <h1>Near Me - Find Deals In Your Local Area</h1>
            <p>
              Save Up to 80% on local deals and activities near you. Whether
              you&apos;re heading out on a day trip or want to find something in
              your local area to do, we have discounts for the best restaurants,
              zoos, theme parks and more!
            </p>
            {!data && <p>No data rendered</p>}
            {data?.map((locations, loc) => {
              return (
                <ItemHub
                  isMobile={isMobile}
                  key={`locations-${locations.region}-${loc}`}
                  locations={locations}
                />
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        .rel-container {
          position: relative;
          font-family: 'Open Sans', sans-serif;
        }
        .container {
          margin-top: 0;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .container {
            margin-top: 20px;
            margin-bottom: 20px;
          }
        }
        .seo__content {
          padding: 10px;
          background-color: ${theme.commoncolors.white};
          background-repeat: no-repeat;
          background-position: right -45px top -45px;
          background-size: 125px 125px;
          border-radius: 0;
          margin-bottom: 0;
          margin-left: -15px;
          margin-right: -15px;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .seo__content {
            padding: 10px 30px;
            box-shadow: ${theme.boxshadow};
            border-radius: 6px;
            margin-bottom: 24px;
            margin-left: 0;
            margin-right: 0;
          }
        }

        .seo__content :global(h1) {
          color: ${theme.colors.primaryonwhite};
          font-size: 22px;
          text-align: left;
          font-weight: bold;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .seo__content :global(h1) {
            font-size: 26px;
            text-align: center;
            line-height: 64px;
          }
        }

        .seo__content :global(.hero-image) {
          background-size: cover;
          background-position: center;
          margin-top: 0;
          height: 180px;
          position: absolute;
          width: 100%;
          top: 0;
          left: 0;
          z-index: -1;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .seo__content :global(.hero-image) {
            margin-top: -20px;
            height: 370px;
          }
        }

        .seo__content :global(h3) {
          color: ${theme.colors.primaryonwhite};
          font-weight: bold;
          font-size: 22px;
          line-height: 24px;
        }

        .seo__content :global(h5) {
          font-weight: bold;
          font-size: 18px;
          line-height: 20px;
        }

        .seo__content :global(h6) {
          font-size: 14px;
          line-height: 16px;
        }
        /* Mobile Image - 736px x 414px */
        .hero-image {
          background-image: url(https://static.wowcher.co.uk/binaries/seo-lp-guides-shero.jpg);
        }
        /* Large Image - 1440px x 500px */
        @media (min-width: 767px) {
          .hero-image {
            background-image: url(https://static.wowcher.co.uk/binaries/seo-lp-guides-lhero.jpg);
          }
        }
      `}</style>
    </>
  );
};

NearMePage.propTypes = {
  data: PropTypes.array,
};

export const getServerSideProps = wrapper.getServerSideProps(
  async (context) => {
    const { req, store } = context;
    await store.dispatch(getServerSideEssentials(req.url)).catch((error) => {
      console.log(`getServerSideEssentials error: ${error}`);
    });

    return getNearMeHub(context);
  },
);

export default NearMePage;
